/** @typedef {import('@basqet-dashboard/common/types/compliance').KycRecord} KycRecord */
/** @typedef {import('@basqet-dashboard/common/types/compliance').ComplianceUploadResponse} ComplianceUploadResponse */
/** @typedef {import('@basqet-dashboard/common/types/compliance').ComplianceSaveResponse} ComplianceSaveResponse */
/** @typedef {import('@basqet-dashboard/common/types/compliance').ComplianceSubmitResponse} ComplianceSubmitResponse */
/** @typedef {import('@basqet-dashboard/common/types/compliance').MerchantType} MerchantType */

import {
  deleteComplianceEntry,
  getComplianceProfile,
  initComplianceKyc,
  restartComplianceKyc,
  saveComplianceSection,
  submitComplianceForReview,
  uploadComplianceAttachment
} from '@basqet-dashboard/common/api';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

const getValidationToastMessage = (validationError) => {
  if (!validationError || typeof validationError !== 'object') return '';

  const message = String(validationError.message ?? '').trim();
  if (message) return message;

  const field = String(validationError.field ?? '').trim();
  if (field) return `Please review ${field}.`;

  return '';
};

const showValidationToasts = (errors, fallbackMessage) => {
  const messages = [
    ...new Set(
      (Array.isArray(errors) ? errors : [])
        .map(getValidationToastMessage)
        .filter((message) => message.length > 0)
    )
  ];

  if (messages.length === 0) {
    toast.error(fallbackMessage);
    return;
  }

  messages.forEach((message) => {
    toast.error(message, { autoClose: 5000 });
  });
};

/**
 * Custom hook for managing compliance/KYC operations
 * @returns {{loading: boolean, error: string | null, initializeKyc: (merchantType: MerchantType) => Promise<KycRecord>, loadProfile: () => Promise<KycRecord | null>, uploadFile: (file: File) => Promise<ComplianceUploadResponse>, saveSection: (sectionKey: string, data: object, options?: {entryId?: string, version?: number, position?: number}) => Promise<ComplianceSaveResponse>, deleteEntry: (sectionKey: string, entryId: string) => Promise<void>, submitForReview: (idempotencyKey: string) => Promise<ComplianceSubmitResponse>}}
 */
export const useCompliance = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Initialize KYC record for merchant
   */
  const initializeKyc = useCallback(async (merchantType) => {
    try {
      setLoading(true);
      setError(null);
      const kyc = await initComplianceKyc(merchantType);
      return kyc;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to initialize KYC';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
      /* v8 ignore next */
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load existing KYC profile
   */
  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const profile = await getComplianceProfile();
      return profile;
    } catch (err) {
      // If KYC doesn't exist, return null instead of throwing
      if (err.response?.status === 404) {
        return null;
      }
      const errorMessage = err.response?.data?.message || 'Failed to load KYC profile';
      setError(errorMessage);
      throw err;
      /* v8 ignore next */
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Upload a file attachment
   */
  const uploadFile = useCallback(async (file) => {
    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      const result = await uploadComplianceAttachment(formData);
      return result;
    } catch (err) {
      let errorMessage = 'Failed to upload file';

      if (err.response?.status === 413) {
        errorMessage = 'File size exceeds 5MB limit';
      } else if (err.response?.status === 422) {
        errorMessage = err.response?.data?.message || 'Invalid file type';
      } else {
        errorMessage = err.response?.data?.message || errorMessage;
      }

      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
      /* v8 ignore next */
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save section data
   */
  const saveSection = useCallback(async (sectionKey, data, options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const body = {
        ...data,
        ...(options.entryId && { entry_id: options.entryId }),
        ...(options.version !== undefined && { version: options.version }),
        ...(options.position !== undefined && { position: options.position })
      };

      const result = await saveComplianceSection(sectionKey, body);
      return result;
    } catch (err) {
      const errors = err.response?.data?.data || [];
      const errorMessage = err.response?.data?.message || 'Failed to save section';

      setError(errorMessage);
      showValidationToasts(errors, errorMessage);

      throw err;
      /* v8 ignore next */
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete an entry from a multi-entry section
   */
  const deleteEntry = useCallback(async (sectionKey, entryId) => {
    try {
      setLoading(true);
      setError(null);
      await deleteComplianceEntry(sectionKey, entryId);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete entry';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
      /* v8 ignore next */
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Restart (reset) the current KYC record
   */
  const restartKyc = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await restartComplianceKyc();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to restart KYC';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
      /* v8 ignore next */
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Submit KYC for review
   */
  const submitForReview = useCallback(async (idempotencyKey) => {
    try {
      setLoading(true);
      setError(null);

      const result = await submitComplianceForReview(idempotencyKey);
      toast.success('KYC submitted successfully for review');
      return result;
    } catch (err) {
      const errors = err.response?.data?.data || [];
      const errorMessage = err.response?.data?.message || 'Failed to submit for review';

      setError(errorMessage);
      showValidationToasts(errors, errorMessage);

      throw err;
      /* v8 ignore next */
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    initializeKyc,
    loadProfile,
    uploadFile,
    saveSection,
    deleteEntry,
    restartKyc,
    submitForReview
  };
};
